// src/pages/CandidateManagementPage.jsx
import React, { useEffect, useState } from 'react';
import SidebarMenu from "../components/SidebarMenu";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Tag } from "primereact/tag";
import { InputNumber } from "primereact/inputnumber";
import { FileUpload } from "primereact/fileupload";
import { FilterMatchMode } from "primereact/api";
import { formatDateTR } from "../utils/filter_lib";
import api from "../services/api";
import { toast } from "react-toastify";
import { extractFieldErrors } from "../utils/formErrorUtils";

const STATUS_OPTIONS = [
  { label: "Applied", value: "applied" },
  { label: "Interviewing", value: "interviewing" },
  { label: "Hired", value: "hired" },
  { label: "Rejected", value: "rejected" },
];

// Eğer endpoint isimlerin farklıysa, sadece burayı değiştirmen yeterli
const ENDPOINTS = {
  list:            "/candidates",
  detail: (id)     => `/candidates/${id}`,
  interviews: (id, interviewId) =>
    interviewId
      ? `/candidates/${id}/interviews/${interviewId}`
      : `/candidates/${id}/interviews`,
  cvView:   (id)   => `/candidates/${id}/cv`,
  cvDownload:(id)  => `/candidates/${id}/cv/download`,
  cvUpload: (id)   => `/candidates/${id}/cv/upload`,

  // Skills tarafı (gerekiyorsa isimleri uyarla)
  skillsMaster:    "/skills",                            // tüm skill master listesi
  candidateSkills: (id) => `/candidates/${id}/skills`,    // GET list, POST add
  deleteSkill: (id, skillId) => `/candidates/${id}/skills/${skillId}`, // DELETE
};

const emptyCandidate = {
  first_name: "",
  last_name: "",
  email: "",
  phone_number: "",
  application_date: null,
  interview_date: null,
  job_posting_url: "",
  status: "applied",
};

function CandidateManagementPage() {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [selected, setSelected] = useState(null);

  const [detailVisible, setDetailVisible] = useState(false);
  const [createVisible, setCreateVisible] = useState(false);
  const [interviewVisible, setInterviewVisible] = useState(false);
  const [interviewsVisible, setInterviewsVisible] = useState(false);
  const [interviews, setInterviews] = useState([]);
  const [editingInterview, setEditingInterview] = useState(null);

  const [editModel, setEditModel] = useState(emptyCandidate);
  const [createModel, setCreateModel] = useState(emptyCandidate);
  const [createErrors, setCreateErrors] = useState({});
  const [interviewModel, setInterviewModel] = useState({ scheduled_at: null, notes: "", result: "" });

  // Skills state
  const [allSkills, setAllSkills] = useState([]);              // dropdown için {label, value}
  const [candidateSkills, setCandidateSkills] = useState([]);  // tablo
  const [newSkill, setNewSkill] = useState({ skill_id: null, level: 1 });
  const [cvFile, setCvFile] = useState(null);

  useEffect(() => { fetchCandidates(); }, []);

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    setFilters((prev) => ({ ...prev, global: { value, matchMode: FilterMatchMode.CONTAINS } }));
  };

  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const res = await api.get(ENDPOINTS.list);
      setCandidates(res.data || []);
    } catch (err) {
      console.error("Aday listesi alınamadı:", err);
    } finally {
      setLoading(false);
    }
  };

  // ---- Detail open + candidate skills + master skills ----
  const openDetail = async (row) => {
    setSelected(row);
    setCvFile(null);
    setEditModel({
      ...row,
      application_date: row.application_date ? new Date(row.application_date) : null,
      interview_date: row.interview_date ? new Date(row.interview_date) : null,
    });
    setDetailVisible(true);

    // Master skills (dropdown)
    try {
      const res = await api.get(ENDPOINTS.skillsMaster);
      setAllSkills((res.data || []).map(s => ({ label: s.name ?? s.title ?? `#${s.id}`, value: s.id })));
    } catch (e) {
      console.error("Skill master listesi alınamadı:", e);
      setAllSkills([]);
    }

    // Candidate skills (liste)
    loadCandidateSkills(row.id);
  };

  const loadCandidateSkills = async (candidateId) => {
    try {
      const res = await api.get(ENDPOINTS.candidateSkills(candidateId));
      // Beklenen örnek şekli: [{skill_id, skill_name, level}] veya {skill:{id,name}, level}
      const list = (res.data || []).map(item => {
        if (item.skill) {
          return { skill_id: item.skill.id, skill_name: item.skill.name, level: item.level };
        }
        return { skill_id: item.skill_id, skill_name: item.skill_name, level: item.level };
      });
      setCandidateSkills(list);
    } catch (e) {
      console.error("Aday skill listesi alınamadı:", e);
      setCandidateSkills([]);
    }
  };

  const handleCreateChange = (field, value) => {
    setCreateModel(m => ({ ...m, [field]: value }));
    setCreateErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const loadInterviews = async (candidateId) => {
    if (!candidateId) return;
    try {
      const res = await api.get(ENDPOINTS.interviews(candidateId));
      setInterviews(res.data || []);
    } catch (e) {
      console.error("Görüşmeler alınamadı:", e);
      setInterviews([]);
    }
  };

  const saveEdit = async () => {
    if (!selected) return;
    try {
      await api.put(ENDPOINTS.detail(selected.id), {
        first_name: editModel.first_name || null,
        last_name: editModel.last_name || null,
        email: editModel.email || null,
        phone_number: editModel.phone_number || null,
        job_posting_url: editModel.job_posting_url || null,
        status: editModel.status || null,
        application_date: editModel.application_date ? editModel.application_date.toISOString() : null,
        interview_date: editModel.interview_date ? editModel.interview_date.toISOString() : null,
      });
      setDetailVisible(false);
      fetchCandidates();
    } catch (err) {
      console.error("Aday güncellenemedi:", err);
    }
  };

  const deleteCandidate = async () => {
    if (!selected) return;
    try {
      await api.delete(ENDPOINTS.detail(selected.id));
      setDetailVisible(false);
      fetchCandidates();
    } catch (err) {
      console.error("Aday silinemedi:", err);
    }
  };

  const createCandidate = async () => {
    try {
      await api.post(ENDPOINTS.list, {
        first_name: createModel.first_name,
        last_name: createModel.last_name,
        email: createModel.email,
        phone_number: createModel.phone_number || null,
        job_posting_url: createModel.job_posting_url || null,
        status: createModel.status || "applied",
        application_date: createModel.application_date ? createModel.application_date.toISOString() : null,
        interview_date: createModel.interview_date ? createModel.interview_date.toISOString() : null,
      });
      setCreateVisible(false);
      setCreateModel(emptyCandidate);
      setCreateErrors({});
      fetchCandidates();
    } catch (err) {
      if (err?.response?.status === 422) {
        const fieldErrors = {};
        extractFieldErrors(err).forEach(({ field, message }) => {
          fieldErrors[field] = message;
        });
        setCreateErrors(fieldErrors);
      }
    }
  };

  const scheduleInterview = async () => {
    if (!selected) return;
    try {
      await api.post(ENDPOINTS.interviews(selected.id), {
        candidate_id: selected.id,
        scheduled_at: interviewModel.scheduled_at ? interviewModel.scheduled_at.toISOString() : null,
        result: interviewModel.result || null,
        notes: interviewModel.notes || null,
      });
      setInterviewVisible(false);
      fetchCandidates();
    } catch (err) {
      console.error("Görüşme planlanamadı:", err);
    }
  };

  const updateInterview = async () => {
    if (!selected || !editingInterview) return;
    try {
      await api.put(ENDPOINTS.interviews(selected.id, editingInterview.id), {
        scheduled_at: editingInterview.scheduled_at ? editingInterview.scheduled_at.toISOString() : null,
        result: editingInterview.result || null,
        notes: editingInterview.notes || null,
      });
      setEditingInterview(null);
      loadInterviews(selected.id);
    } catch (e) {
      console.error("Görüşme güncellenemedi:", e);
    }
  };

  const deleteInterview = async (interviewId) => {
    if (!selected) return;
    try {
      await api.delete(ENDPOINTS.interviews(selected.id, interviewId));
      loadInterviews(selected.id);
    } catch (e) {
      console.error("Görüşme silinemedi:", e);
    }
  };

  // ---- Skills: add/remove ----
  const addSkill = async () => {
    if (!selected || !newSkill.skill_id) return;
    try {
      await api.post(ENDPOINTS.candidateSkills(selected.id), {
        skill_id: newSkill.skill_id,
        level: newSkill.level ?? 1,
      });
      setNewSkill({ skill_id: null, level: 1 });
      loadCandidateSkills(selected.id);
    } catch (e) {
      console.error("Skill eklenemedi:", e);
    }
  };

  const removeSkill = async (skill_id) => {
    if (!selected) return;
    try {
      await api.delete(ENDPOINTS.deleteSkill(selected.id, skill_id));
      loadCandidateSkills(selected.id);
    } catch (e) {
      console.error("Skill silinemedi:", e);
    }
  };

  // ---- CV: blob ile görüntüleme / indirme (Auth header ile) ----
  const viewCV = async (row) => {
    const id = row?.id ?? selected?.id;
    if (!id) return;
    try {
      const res = await api.get(ENDPOINTS.cvView(id), { responseType: 'blob', headers: { Accept: 'application/pdf' } });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      toast.success("CV görüntülendi.");
      // İstersen: setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      console.error("CV görüntülenemedi:", e);
      toast.error("CV görüntülenemedi. (Auth?)");
    }
  };

  const downloadCV = async (row) => {
    const id = row?.id ?? selected?.id;
    if (!id) return;
    try {
      const res = await api.get(ENDPOINTS.cvDownload(id), { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `candidate_${id}_cv.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("CV indirildi.");
      // URL.revokeObjectURL(url);
    } catch (e) {
      console.error("CV indirilemedi:", e);
      toast.error("CV indirilemedi. (Auth?)");
    }
  };

  const uploadCV = async () => {
    if (!selected || !cvFile) return;
    const formData = new FormData();
    formData.append('file', cvFile);
    try {
      await api.post(ENDPOINTS.cvUpload(selected.id), formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('CV başarıyla yüklendi.');
      setCvFile(null);
      fetchCandidates();
    } catch (e) {
      console.error('CV yüklenemedi:', e);
      toast.error('CV yüklenemedi.');
    }
  };

  const statusBody = (row) => {
    const map = { applied: "info", interviewing: "warning", hired: "success", rejected: "danger" };
    return <Tag value={row.status} severity={map[row.status] || "info"} />;
  };
  const dateBody = (field) => (row) => formatDateTR(row[field]);

  const header = (
    <div className="flex justify-between align-items-center">
      <h3>Adaylar</h3>
      <div className="flex gap-2 align-items-center">
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={filters.global.value || ""}
            onChange={onGlobalFilterChange}
            placeholder="Ara..."
            style={{ width: 220 }}
          />
        </span>
        <Button label="Yenile" icon="pi pi-refresh" onClick={fetchCandidates} severity="secondary" />
        <Button label="Yeni Aday" icon="pi pi-plus" onClick={() => setCreateVisible(true)} />
        <Button
          label="Görüşme Planla"
          icon="pi pi-calendar-plus"
          severity="help"
          disabled={!selected}
          onClick={() => setInterviewVisible(true)}
        />
      </div>
    </div>
  );

  return (
    <div className="layout">
      <SidebarMenu />
      <main className="content p-4">
          <DataTable
            value={candidates}
            loading={loading}
            header={header}
            paginator
            rows={10}
            stripedRows
            selectionMode="single"
            selection={selected}
            onSelectionChange={(e) => setSelected(e.value)}
            onRowDoubleClick={(e) => openDetail(e.data)}
            dataKey="id"
            filters={filters}
            globalFilterFields={["first_name", "last_name", "email", "job_posting_url", "status"]}
            responsiveLayout="scroll"
          >
          <Column field="first_name" header="İsim" sortable />
          <Column field="last_name" header="Soyisim" sortable />
          <Column field="email" header="E-posta" sortable />
          <Column field="job_posting_url" header="İlan URL" body={(r)=> r.job_posting_url ? <a href={r.job_posting_url} target="_blank" rel="noreferrer">Link</a> : "-"} />
          <Column field="application_date" header="Başvuru Tarihi" body={dateBody("application_date")} sortable />
          <Column field="interview_date" header="Görüşme Tarihi" body={dateBody("interview_date")} sortable />
          <Column field="status" header="Durum" body={statusBody} sortable />
          <Column
            header="İşlemler"
            body={(row) => (
              <div className="flex gap-2">
                <Button icon="pi pi-eye" rounded text onClick={() => openDetail(row)} />
                <Button icon="pi pi-file" rounded text onClick={() => viewCV(row)} />
                <Button icon="pi pi-download" rounded text onClick={() => downloadCV(row)} />
              </div>
            )}
          />
        </DataTable>

        {/* Detay / Düzenleme + Yetenekler */}
        <Dialog
          header="Aday Detayı"
          visible={detailVisible}
          style={{ width: 720 }}
          modal
          onHide={() => setDetailVisible(false)}
          footer={
            <div className="flex justify-end gap-2">
              <Button label="Sil" icon="pi pi-trash" severity="danger" onClick={deleteCandidate} />
              <Button label="Kaydet" icon="pi pi-check" onClick={saveEdit} />
            </div>
          }
        >
          <div className="grid gap-4">
            {/* Kişisel bilgiler */}
            <div className="flex gap-2">
              <span className="p-float-label flex-1">
                <InputText id="first_name" value={editModel.first_name || ""} onChange={(e)=>setEditModel(m=>({...m, first_name: e.target.value}))}/>
                <label htmlFor="first_name">İsim</label>
              </span>
              <span className="p-float-label flex-1">
                <InputText id="last_name" value={editModel.last_name || ""} onChange={(e)=>setEditModel(m=>({...m, last_name: e.target.value}))}/>
                <label htmlFor="last_name">Soyisim</label>
              </span>
            </div>

            <div className="flex gap-2">
              <span className="p-float-label flex-1">
                <InputText id="email" value={editModel.email || ""} onChange={(e)=>setEditModel(m=>({...m, email: e.target.value}))}/>
                <label htmlFor="email">E-posta</label>
              </span>
              <span className="p-float-label flex-1">
                <InputText id="phone_number" value={editModel.phone_number || ""} onChange={(e)=>setEditModel(m=>({...m, phone_number: e.target.value}))}/>
                <label htmlFor="phone_number">Telefon</label>
              </span>
            </div>

            <div className="flex gap-2">
              <span className="p-float-label flex-1">
                <Calendar id="application_date" value={editModel.application_date} onChange={(e)=>setEditModel(m=>({...m, application_date: e.value}))} dateFormat="dd.mm.yy" showIcon />
                <label htmlFor="application_date">Başvuru Tarihi</label>
              </span>
              <span className="p-float-label flex-1">
                <Calendar id="interview_date" value={editModel.interview_date} onChange={(e)=>setEditModel(m=>({...m, interview_date: e.value}))} dateFormat="dd.mm.yy" showIcon />
                <label htmlFor="interview_date">Görüşme Tarihi</label>
              </span>
            </div>

            <div className="flex gap-2">
              <span className="p-float-label flex-1">
                <InputText id="job_posting_url" value={editModel.job_posting_url || ""} onChange={(e)=>setEditModel(m=>({...m, job_posting_url: e.target.value}))}/>
                <label htmlFor="job_posting_url">İlan URL</label>
              </span>
              <span className="p-float-label" style={{ width: 220 }}>
                <Dropdown id="status" value={editModel.status} options={STATUS_OPTIONS} optionLabel="label" optionValue="value" onChange={(e)=>setEditModel(m=>({...m, status: e.value}))}/>
                <label htmlFor="status">Durum</label>
              </span>
            </div>

            {/* CV & Görüşme */}
            <div className="flex gap-2 flex-wrap align-items-center justify-end">
              <FileUpload
                mode="basic"
                chooseLabel="CV Seç"
                auto
                customUpload
                accept="application/pdf"
                uploadHandler={(e) => setCvFile(e.files[0])}
              />
              <Button label="CV Yükle" icon="pi pi-upload" onClick={uploadCV} disabled={!cvFile} text />
              <Button label="CV Görüntüle" icon="pi pi-file" onClick={() => viewCV()} text />
              <Button label="CV İndir" icon="pi pi-download" onClick={() => downloadCV()} text />
              <Button label="Görüşmeleri Yönet" icon="pi pi-list" onClick={() => { loadInterviews(selected.id); setInterviewsVisible(true); }} text />
              <Button label="Görüşme Planla" icon="pi pi-calendar-plus" onClick={()=>setInterviewVisible(true)} text />
            </div>

            {/* Yetenekler bölümü */}
            <div className="border-round surface-border p-3" style={{ border: '1px solid var(--surface-border)' }}>
              <h4 className="mt-0">Yetenekler</h4>

              {/* Ekleme satırı */}
              <div className="flex gap-2 mb-3">
                <span className="p-float-label" style={{ minWidth: 260 }}>
                  <Dropdown
                    id="skill_select"
                    value={newSkill.skill_id}
                    options={allSkills}
                    onChange={(e)=>setNewSkill(m=>({...m, skill_id: e.value}))}
                    placeholder="Yetenek seçin"
                    filter
                  />
                  <label htmlFor="skill_select">Yetenek</label>
                </span>
                <span className="p-float-label" style={{ width: 160 }}>
                  <InputNumber
                    id="skill_level"
                    value={newSkill.level}
                    onValueChange={(e)=>setNewSkill(m=>({...m, level: e.value ?? 1}))}
                    min={1}
                    max={10}
                    showButtons
                  />
                  <label htmlFor="skill_level">Seviye (1-10)</label>
                </span>
                <Button label="Ekle" icon="pi pi-plus" onClick={addSkill} disabled={!newSkill.skill_id} />
              </div>

              {/* Adayın skill listesi */}
              <DataTable value={candidateSkills} stripedRows emptyMessage="Yetenek yok">
                <Column field="skill_name" header="Yetenek" />
                <Column field="level" header="Seviye" />
                <Column header="İşlemler" body={(row) => (
                  <Button icon="pi pi-trash" text severity="danger" onClick={() => removeSkill(row.skill_id)} />
                )} />
              </DataTable>
            </div>
          </div>
        </Dialog>

        {/* Yeni Aday Modal */}
        <Dialog
          header="Yeni Aday"
          visible={createVisible}
          style={{ width: 640 }}
          modal
          onHide={() => setCreateVisible(false)}
          footer={<Button label="Ekle" icon="pi pi-check" onClick={createCandidate} />}
        >
          <div className="grid gap-3">
            <div className="flex gap-2">
              <span className="p-float-label flex-1">
                <InputText id="c_first_name" value={createModel.first_name} onChange={(e)=>handleCreateChange('first_name', e.target.value)} className={createErrors.first_name ? 'p-invalid' : ''}/>
                <label htmlFor="c_first_name">İsim*</label>
                {createErrors.first_name && <small className="p-error">{createErrors.first_name}</small>}
              </span>
              <span className="p-float-label flex-1">
                <InputText id="c_last_name" value={createModel.last_name} onChange={(e)=>handleCreateChange('last_name', e.target.value)} className={createErrors.last_name ? 'p-invalid' : ''}/>
                <label htmlFor="c_last_name">Soyisim*</label>
                {createErrors.last_name && <small className="p-error">{createErrors.last_name}</small>}
              </span>
            </div>

            <div className="flex gap-2">
              <span className="p-float-label flex-1">
                <InputText id="c_email" value={createModel.email} onChange={(e)=>handleCreateChange('email', e.target.value)} className={createErrors.email ? 'p-invalid' : ''}/>
                <label htmlFor="c_email">E-posta*</label>
                {createErrors.email && <small className="p-error">{createErrors.email}</small>}
              </span>
              <span className="p-float-label flex-1">
                <InputText id="c_phone" value={createModel.phone_number} onChange={(e)=>handleCreateChange('phone_number', e.target.value)} className={createErrors.phone_number ? 'p-invalid' : ''}/>
                <label htmlFor="c_phone">Telefon</label>
                {createErrors.phone_number && <small className="p-error">{createErrors.phone_number}</small>}
              </span>
            </div>

            <div className="flex gap-2">
              <span className="p-float-label flex-1">
                <Calendar id="c_application_date" value={createModel.application_date} onChange={(e)=>handleCreateChange('application_date', e.value)} dateFormat="dd.mm.yy" showIcon className={createErrors.application_date ? 'p-invalid' : ''} />
                <label htmlFor="c_application_date">Başvuru Tarihi</label>
                {createErrors.application_date && <small className="p-error">{createErrors.application_date}</small>}
              </span>
              <span className="p-float-label flex-1">
                <Calendar id="c_interview_date" value={createModel.interview_date} onChange={(e)=>handleCreateChange('interview_date', e.value)} dateFormat="dd.mm.yy" showIcon className={createErrors.interview_date ? 'p-invalid' : ''} />
                <label htmlFor="c_interview_date">Görüşme Tarihi</label>
                {createErrors.interview_date && <small className="p-error">{createErrors.interview_date}</small>}
              </span>
            </div>

            <div className="flex gap-2">
              <span className="p-float-label flex-1">
                <InputText id="c_job_posting_url" value={createModel.job_posting_url} onChange={(e)=>handleCreateChange('job_posting_url', e.target.value)} className={createErrors.job_posting_url ? 'p-invalid' : ''}/>
                <label htmlFor="c_job_posting_url">İlan URL</label>
                {createErrors.job_posting_url && <small className="p-error">{createErrors.job_posting_url}</small>}
              </span>
              <span className="p-float-label" style={{ width: 220 }}>
                <Dropdown id="c_status" value={createModel.status} options={STATUS_OPTIONS} optionLabel="label" optionValue="value" onChange={(e)=>handleCreateChange('status', e.value)} className={createErrors.status ? 'p-invalid' : ''}/>
                <label htmlFor="c_status">Durum</label>
                {createErrors.status && <small className="p-error">{createErrors.status}</small>}
              </span>
            </div>
          </div>
        </Dialog>

        {/* Görüşmeler Modal */}
        <Dialog
          header="Görüşmeler"
          visible={interviewsVisible}
          style={{ width: 720 }}
          modal
          onHide={() => { setInterviewsVisible(false); setEditingInterview(null); setInterviews([]); }}
        >
          <DataTable value={interviews} stripedRows emptyMessage="Görüşme yok">
            <Column field="scheduled_at" header="Tarih" body={(r) => formatDateTR(r.scheduled_at)} />
            <Column field="result" header="Sonuç" />
            <Column field="notes" header="Notlar" />
            <Column
              header="İşlemler"
              body={(row) => (
                <div className="flex gap-2">
                  <Button
                    icon="pi pi-pencil"
                    text
                    onClick={() =>
                      setEditingInterview({
                        ...row,
                        scheduled_at: row.scheduled_at ? new Date(row.scheduled_at) : null,
                      })
                    }
                  />
                  <Button icon="pi pi-trash" text severity="danger" onClick={() => deleteInterview(row.id)} />
                </div>
              )}
            />
          </DataTable>

          {editingInterview && (
            <div className="grid gap-3 mt-4">
              <span className="p-float-label">
                <Calendar
                  id="e_scheduled_at"
                  value={editingInterview.scheduled_at}
                  onChange={(e) => setEditingInterview((m) => ({ ...m, scheduled_at: e.value }))}
                  showTime
                  showIcon
                />
                <label htmlFor="e_scheduled_at">Tarih & Saat</label>
              </span>
              <span className="p-float-label">
                <InputText
                  id="e_result"
                  value={editingInterview.result || ""}
                  onChange={(e) => setEditingInterview((m) => ({ ...m, result: e.target.value }))}
                />
                <label htmlFor="e_result">Sonuç</label>
              </span>
              <span className="p-float-label">
                <InputText
                  id="e_notes"
                  value={editingInterview.notes || ""}
                  onChange={(e) => setEditingInterview((m) => ({ ...m, notes: e.target.value }))}
                />
                <label htmlFor="e_notes">Notlar</label>
              </span>
              <div className="flex justify-end gap-2">
                <Button label="İptal" text onClick={() => setEditingInterview(null)} />
                <Button label="Kaydet" icon="pi pi-check" onClick={updateInterview} />
              </div>
            </div>
          )}
        </Dialog>

        {/* Görüşme Planlama Modal */}
        <Dialog
          header="Görüşme Planla"
          visible={interviewVisible}
          style={{ width: 520 }}
          modal
          onHide={() => setInterviewVisible(false)}
          footer={<Button label="Kaydet" icon="pi pi-check" onClick={scheduleInterview} />}
        >
          <div className="grid gap-3">
            <span className="p-float-label">
              <Calendar
                id="i_scheduled_at"
                value={interviewModel.scheduled_at}
                onChange={(e)=>setInterviewModel(m=>({...m, scheduled_at: e.value}))}
                showTime
                showIcon
              />
              <label htmlFor="i_scheduled_at">Tarih & Saat*</label>
            </span>
            <span className="p-float-label">
              <InputText id="i_result" value={interviewModel.result} onChange={(e)=>setInterviewModel(m=>({...m, result: e.target.value}))}/>
              <label htmlFor="i_result">Sonuç (opsiyonel)</label>
            </span>
            <span className="p-float-label">
              <InputText id="i_notes" value={interviewModel.notes} onChange={(e)=>setInterviewModel(m=>({...m, notes: e.target.value}))}/>
              <label htmlFor="i_notes">Notlar</label>
            </span>
          </div>
        </Dialog>
      </main>
    </div>
  );
}

export default CandidateManagementPage;
